import { useLocation, useMatches, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { errorCode } from '@/api/errors';
import { asInternalHref } from '@/auth/redirectHref';
import { useViewAsSession } from '@/viewas';
import { useCurrentUser } from '~/hooks/useCurrentUser';

/**
 * Redirects signed-out visitors to /login with a return-to, and signed-in
 * visitors away from /login and /register.
 */
export function useAuthCheck(): void {
  const navigate = useNavigate();
  const matches = useMatches();
  // Pathname, search and hash with no origin: the shape `navigate({ href })`
  // round-trips.
  const location = useLocation();
  const currentHref = location.href;
  // The return-to a bounced visitor carried in, re-validated here because this
  // hook also runs on /register, whose `validateSearch` does not know the key.
  const redirectHref = asInternalHref(
    (location.search as { redirect?: unknown }).redirect,
  );
  // Checked against both the URL and the route matches, because they update on
  // different renders and each lags in one direction:
  //
  // - Arriving at /login, the URL leads while `matches` still names the
  //   previous route; a matches-only test would bounce again and store the
  //   login URL as its own return-to, a login loop.
  // - Leaving /login for /first-time-password-reset/, the pathname moves
  //   before `matches` marks the new page public; a pathname-only test would
  //   bounce the visitor back to login.
  //
  // Neither covers the other, so the guard is the union.
  const onAuthPath = /^\/(login|register)(\/|$)/.test(location.pathname);
  const inAuthLayout = matches.some((match) =>
    (match.routeId as string).startsWith('/_auth'),
  );
  const { me: user, isSuccess, failureReason } = useCurrentUser();
  const { isViewing, sessionPending } = useViewAsSession();

  // Exact route-id equality, not a prefix match, so every public page needs its
  // own entry here, or an anonymous visitor is bounced to /login as soon as
  // `getCurrentUser` 401s.
  const isPublicPage = matches.some(
    (match) =>
      match.routeId === '/study-helper-application' ||
      // The two follow-up application forms are anonymous, as in legacy, and
      // they are the only way to become a follow-up coordinator or partner.
      match.routeId === '/follow-up-coordinator-application' ||
      match.routeId === '/follow-up-partner-application' ||
      // Front's plugin iframe has no WBS session; the page is gated by the
      // Front secret.
      match.routeId === '/front/sidebar' ||
      // Reached by redirect from login when the server answers
      // `password_reset_required`, so the visitor is signed out. Without this
      // the 401 bounces them straight back to /login/.
      match.routeId === '/_auth/first-time-password-reset' ||
      // An admin's preview of a course version: its cookie is the credential,
      // and the admin is usually not signed in to the web app.
      match.routeId.startsWith('/preview/'),
  );
  const isSuspendedPage = matches.some(
    (match) => match.routeId === '/_app/suspended',
  );

  const error_code = errorCode(failureReason);

  useEffect(() => {
    if (
      !onAuthPath &&
      !inAuthLayout &&
      !isPublicPage &&
      error_code === 'invalid_auth_token'
    ) {
      // Remember where they were headed, as legacy did, so a deep link such as
      // the single-use email verification link survives the login.
      //
      // `search` rather than a hand-built query string, so `_auth.login.tsx`'s
      // `validateSearch` guards the value on the way back.
      navigate({
        to: '/login/',
        search: { redirect: currentHref },
        replace: true,
      });
    }

    // Redirect signed-in users away from the login page, honouring the
    // return-to here too: `useLogin` fills the `getCurrentUser` cache, so this
    // effect fires on the same render as the form's own navigation, and a bare
    // `navigate({ to: '/' })` would land last and discard the return-to.
    if (onAuthPath && isSuccess && user) {
      if (redirectHref !== undefined) {
        navigate({ href: redirectHref, replace: true });
      } else {
        navigate({ to: '/' });
      }
    }

    // Suspended users are bounced to the notice page from every other route,
    // except admins; a non-suspended user may still view /suspended. The API
    // doesn't check suspension, so this redirect is the whole enforcement, as
    // on mobile.
    // A staff member viewing as a suspended member is not bounced either.
    if (
      user?.suspended &&
      user.profile_type !== 'ADMIN' &&
      !isViewing &&
      !sessionPending &&
      !isSuspendedPage
    ) {
      navigate({ to: '/suspended/' });
    }
  }, [
    error_code,
    navigate,
    currentHref,
    redirectHref,
    onAuthPath,
    inAuthLayout,
    isPublicPage,
    isSuspendedPage,
    isSuccess,
    isViewing,
    sessionPending,
    user,
  ]);
}
